import { OrderErrorCode, OrderWithContext, Order } from '@/models/order';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import { ui } from '@/constants';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { ServicesContainer } from '@/services';
import moment from 'moment';
import { CheckoutEvent, ErrorEvent } from '@/services/analytics/events';

export interface RequestData {
  pickupDateTime: string;
  deliveryPhoneNumber: string;
  order: OrderWithContext;
}

const PLEASE_TRY_AGAIN = 'Please try again.';
const DIDNT_GET_THAT = `Sorry, we didn't quite get that.`;
const PAYMENT_FAIL = `We're sorry, your payment didn't go through.`;
const ORDER_FAIL = `We're sorry, your order didn't go through.`;
const CONFIRM = 'Okay';
const CLOSE = 'Close';
const SWITCH_TO_PICKUP = 'Switch to pickup';


export function displayOrderSubmitErrorModal(
  errorCode: OrderErrorCode,
  request: RequestData,
  showPaymentIssueModal: (order: OrderWithContext) => void,
  minimumOrderTotalForDelivery: number | null,
  isPickup: boolean,
  appStore: Store<AppState>,
  services: ServicesContainer,
  pickupContainer: HTMLElement | null = null,
): void {
  let header = '';
  let content = '';
  switch (errorCode) {
    case OrderErrorCode.PromotionPickUpOnly:
      if (!isPickup) {
        services.order.showPickUpOnlyPromoErrorModal();
      }
      break;

    case OrderErrorCode.PromotionDeliveryOnly:
      if (isPickup) {
        services.order.showDeliveryOnlyPromoErrorModal();
      }
      break;

    case OrderErrorCode.UnableToReservePickupTime:
      appStore.state.checkout.pickup.pickupTime.error = `${moment(request.pickupDateTime).format('h:mma')} is no longer available`;
      if (isPickup) {
        services.analytics.send(new ErrorEvent('checkout-pickup', `Your pickup time has expired`, 'basic-error'));
        services.ui.setModalVisibility('basicError', true, {
          header: `OOPS, YOUR PICKUP TIME IS NO LONGER AVAILABLE.`,
          content: '',
          options: [
            {
              text: 'Update my pickup time',
              onClick: async () => {
                services.ui.setLastSelectedElement('pickup-time-heading');

                services.ui.setModalVisibility('basicError', false);
                if (pickupContainer) {
                  pickupContainer.scrollIntoView();
                }
                services.ui.incrementLoader('pickupTimes');
                try {
                  services.checkout.setAvailablePickupTimes(
                    await services.sput.getPickupTimesForRestaurant(request.order.order.restaurantId, getEntreeCount(request.order.order))
                  );
                  services.analytics.send(new CheckoutEvent());
                } catch {
                  services.checkout.setAvailablePickupTimes([]);
                }
                services.ui.decrementLoader('pickupTimes');
              },
            },
          ],
        });
      } else {
        services.ui.setModalVisibility('basicError', true, {
          header: `OOPS, YOUR DELIVERY WINDOW EXPIRED.`,
          content: '',
          options: [
            {
              text: 'Update my delivery times',
              onClick: () => {
                services.ui.setModalVisibility('basicError', false);
                location.reload();
              },
            },
          ],
        });
      }
      break;

    case OrderErrorCode.BadLocation:
    case OrderErrorCode.OnlineOrderingDisabledForLocation:
      header = `We couldn't submit your order.`;
      content = 'Please select a different restaurant or delivery address.';
      services.analytics.send(new ErrorEvent('checkout', `${header} ${content} `, 'basic-error'));
      services.ui.setModalVisibility('basicError', true, {
        header,
        content,
        options: [
          {
            text: CLOSE,
            type: ButtonType.Secondary,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
          {
            text: 'Find a Chipotle',
            type: ButtonType.Primary,
            onClick: () => {
              services.ui.setModalVisibility('basicError', false);
              services.ui.setModalVisibility('findAChipotle', true);
            },
          },
        ],
      });
      break;

    case OrderErrorCode.MenuNotAvailableForLocation:
      header = `We're sorry.`;
      content = `Your order wasn't submitted and we weren't able to save your order details. You'll have to restart your order and try again.`;
      services.analytics.send(new ErrorEvent('checkout', `${header} ${content}`, 'basic-error'));
      services.ui.setModalVisibility('basicError', true, {
        header,
        content,
        options: [
          {
            text: CONFIRM,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
        ],
      });
      break;

    case OrderErrorCode.UnableToCalculateDeliveryFee:
      header = `Sorry, we're having troubles with delivery.`,
        content = `Please switch to pickup or try delivery again later.`,
        services.analytics.send(new ErrorEvent('checkout', `${header} ${content}`, 'basic-error'));
      services.ui.setModalVisibility('basicError', true, {
        header,
        content,
        options: [
          {
            text: CLOSE,
            type: ButtonType.Secondary,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
          {
            text: SWITCH_TO_PICKUP,
            onClick: () => {
              services.ui.setModalVisibility('basicError', false);
              services.order.switchToPickup();
            },
          },
        ],
      });
      break;

    case OrderErrorCode.InvalidPhoneNumber:
      services.analytics.send(new ErrorEvent('checkout', 'Phone number not supported. You provided ***-***-**** please use a different number for this address.', 'bad-phone-number'));
      services.ui.setModalVisibility('checkoutBadPhoneNumber', true, {
        phoneNumber: request.deliveryPhoneNumber,
      });
      break;

    case OrderErrorCode.OrderNotSubmitted:
      services.ui.setBannerState({
        message: `Your Order Wasn't Submitted.`,
        details: PLEASE_TRY_AGAIN,
        visible: true,
        backgroundColor: ui.colors.red,
      });
      window.scrollTo(0, 0);
      break;

    case OrderErrorCode.PurchaseNotCompleted:
    case OrderErrorCode.PurchaseNotApproved:
      // Show generic error handling if the order uses promos or split payments
      if ((request.order.discounts && request.order.discounts?.length) || 
          (request.order.order.payments && request.order.order.payments.length > 1)) {
        services.ui.setModalVisibility('basicError', true, {
          header: PAYMENT_FAIL,
          content: PLEASE_TRY_AGAIN,
          options: [
            {
              text: CONFIRM,
              onClick: () => services.ui.setModalVisibility('basicError', false),
            },
          ],
        });
      } else {
        showPaymentIssueModal(request.order);
      }
      break;

    case OrderErrorCode.PickupDateTimeRequired:
      services.ui.setModalVisibility('basicError', true, {
        header: DIDNT_GET_THAT,
        content: `Please double-check the pickup time.`,
        options: [
          {
            text: CONFIRM,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
        ],
      });
      break;

    case OrderErrorCode.PaymentRequired:
      header = DIDNT_GET_THAT,
        content = `Please double-check your payment information and try again.`,
        services.analytics.send(new ErrorEvent('checkout-payment', `${header} ${content}`, 'basic-error'));

      services.ui.setModalVisibility('basicError', true, {
        header,
        content,
        options: [
          {
            text: CONFIRM,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
        ],
      });
      break;

    case OrderErrorCode.PaymentTypeInvalid:
      services.ui.setModalVisibility('basicError', true, {
        header: `Dang, looks like an invalid payment.`,
        content: `Please try again, and if the problem persists attempt online payment with another card.`,
        options: [
          {
            text: CONFIRM,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
        ],
      });
      break;

    case OrderErrorCode.PaymentRequiredWhenDelivery:
      header = DIDNT_GET_THAT;
      content = `Please double-check your payment information. If the problem persists switch to pickup or try again later.`;
      services.analytics.send(new ErrorEvent('checkout-payment', `${header} ${content}`, 'basic-error'));

      services.ui.setModalVisibility('basicError', true, {
        header,
        content,
        options: [
          {
            text: CONFIRM,
            type: ButtonType.Secondary,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
          {
            text: SWITCH_TO_PICKUP,
            onClick: () => {
              services.ui.setModalVisibility('basicError', false);
              services.order.switchToPickup();
            },
          },
        ],
      });
      break;

    case OrderErrorCode.CardholderNameRequired:
      services.ui.setModalVisibility('basicError', true, {
        header: `Oopsies.`,
        content: `Please ensure your cardholder name is correct.`,
        options: [
          {
            text: CONFIRM,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
        ],
      });
      break;

    case OrderErrorCode.CardExpiryRequired:
      services.ui.setModalVisibility('basicError', true, {
        header: PAYMENT_FAIL,
        content: `Please ensure your card's expiration date is correct.`,
        options: [
          {
            text: CONFIRM,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
        ],
      });
      break;

    case OrderErrorCode.PaymentRequiredWhenPromotions:
      services.ui.setModalVisibility('basicError', true, {
        header: ORDER_FAIL,
        content: `Payment is required.`,
        options: [
          {
            text: CONFIRM,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
        ],
      });
      break;

    case OrderErrorCode.PaymentNotAllowedForOutsideUS:
      services.ui.setModalVisibility('basicError', true, {
        header: `Woops! How did that happen?`,
        content: `Sorry, we aren't quite ready to accept payments online for countries outside the US.`,
        options: [
          {
            text: CONFIRM,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
        ],
      });
      break;

    case OrderErrorCode.UnableToCalculatePromoDiscount:
      services.ui.setModalVisibility('basicError', true, {
        header: ORDER_FAIL,
        content: `There was a problem with the promo code. Please try again.`,
        options: [
          {
            text: CONFIRM,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
        ],
      });
      break;

    case OrderErrorCode.OrderDoesNotMeetMinDeliveryValue:
      content = minimumOrderTotalForDelivery
        ? `Please ensure your order meets the minimum delivery requirement of ${services.utils.formatPrice(minimumOrderTotalForDelivery)}.`
        : `Please try again, and if the problem persists switch to pickup.`;

      services.analytics.send(new ErrorEvent('checkout-delivery', `${ORDER_FAIL} ${content}`, 'basic-error'));
      const options = [
        {
          text: CONFIRM,
          type: ButtonType.Secondary,
          onClick: () => services.ui.setModalVisibility('basicError', false),
        },
        {
          text: SWITCH_TO_PICKUP,
          onClick: () => {
            services.ui.setModalVisibility('basicError', false);
            services.order.switchToPickup();
          },
        },
      ];
      services.ui.setModalVisibility('basicError', true, {
        header: ORDER_FAIL,
        content,
        options,
      });
      break;

    case OrderErrorCode.UnableToValidateGiftCardBalance:
      services.ui.setModalVisibility('basicError', true, {
        header: PAYMENT_FAIL,
        content: `There was a problem with your gift card. Please try again.`,
        options: [
          {
            text: CONFIRM,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
        ],
      });
      break;

    case OrderErrorCode.InsufficientGiftCardBalance:
      header = 'Sorry, your gift card balance is not enough to cover this order.';
      content = `You'll have to fill this card up in store before you can use it again.`;
      services.analytics.send(new ErrorEvent('checkout-payment', `${header} ${content}`, 'basic-error'));
      services.ui.setModalVisibility('basicError', true, {
        header,
        content,
        options: [
          {
            text: CONFIRM,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
        ],
      });
      break;

    case OrderErrorCode.OrderNotSubmittedButPaymentCollected:
      header = `Your order was not submitted but your payment method was charged. We're very sorry about this.`,
        content = 'Please contact support to get a refund issued.',
        services.analytics.send(new ErrorEvent('checkout', `Order mishap. ${header} ${content}`, 'basic-error'));
      services.ui.setModalVisibility('basicError', true, {
        header,
        content,
        options: [
          {
            text: CLOSE,
            type: ButtonType.Secondary,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
          {
            text: 'Support',
            type: ButtonType.Primary,
            onClick: () => {
              // TODO-gbes7a: what do?
              window.location.href = services.config.getSupportUrl();
            },
          },
        ],
      });

      break;

    case OrderErrorCode.DeliveryTipMaxExceeded:
      services.analytics.send(new ErrorEvent('checkout-delivery', `whoa, that’s a lotta love. thank you, but tips can’t be more than 50% of the order total.`, 'basic-error'));
      services.ui.setModalVisibility('basicError', true, {
        header: 'HOLY GUACAMOLE',
        content: `This tip is extra generous, but it's over the allowed amount.`,
        options: [
          {
            text: CONFIRM,
            onClick: () => services.ui.setModalVisibility('basicError', false),
          },
        ],
      });
      break;

    case OrderErrorCode.PaymentProviderMismatch:
      services.ui.setModalVisibility('basicError', true, {
        header: 'OOPS, THERE WAS A PROBLEM SUBMITTING YOUR ORDER.',
        content: `Your order wasn't submitted and your card wasn't charged. Please try again.`,
        onClose: () => window.location.reload(),
        options: [
          {
            text: CONFIRM,
            onClick: () => window.location.reload(),
          },
        ],
      });
      break;

    case OrderErrorCode.DonationCampaignExpired:
      services.ui.setModalVisibility('basicError', true, {
        header: 'OH, BUMMER',
        content: `This donation campaign has expired. We’ve removed the donation from your total. Please submit your order again.`,
        onClose: () => window.location.reload(),
        options: [
          {
            text: CONFIRM,
            onClick: async () => {
              try {
                if (request.order.order.donations && request.order.order.donations.length) { // Hopefully this is always true
                  await services.order.deleteDonation(request.order, request.order.order.donations[0].charitableCampaignId);
                }
              } catch (e) {
                services.logger.error('Something went wrong while removing donation for expired campaign from order', { error: e });
              } finally {
                services.ui.setModalVisibility('basicError', false);
              }
            },
          },
        ]
      });
      break;
      
      case OrderErrorCode.UnableToCancelDeliveryWithProvider:
        header = 'OH, BUMMER';
        content = `Your payment failed and we’re having delivery issues. You can switch to Pickup, then check your payment info and retry. `;
        services.analytics.send(new ErrorEvent('checkout-payment', `${header} ${content}`, 'basic-error'));
  
        services.ui.setModalVisibility('basicError', true, {
          header,
          content,
          options: [
            {
              text: SWITCH_TO_PICKUP,
              onClick: () => {
                services.ui.setModalVisibility('basicError', false);
                services.order.switchToPickup();
                services.ui.setModalVisibility('findAChipotle', true, { mode: 'pickup' });
                services.ui.focusDiv('.cmg-modal-container');
              },
            },
          ],
          unmaskContent: true,
        });
        break;
    

    default:
      services.ui.setModalVisibility('globalErrorToast', true);
      break;
  }
}


export function getEntreeCount(order: Order): number {
  if (!order) {
    return 0;
  }

  return (order.meals || []).reduce((acc, m) => acc + (m.entrees || []).length, 0);
}
